import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Keyboard } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import { format, parse, isBefore, isValid, addDays, parseISO, formatISO, } from 'date-fns';
import emoji from 'node-emoji';
import Animated from 'react-native-reanimated';
import { addGoal, deleteGoal, editGoal, getAccountGoals } from 'actions';
import Alert from 'utils/packages/Alert';
import { AppView } from 'design-system/AppView';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { SettingsItemInputMask, SettingsItemMoneyInput, SettingsItemNavigate, } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import StatusBar from 'design-system/StatusBar';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconBoldCalendar, IconGoal, IconTrash } from 'design-system/icons';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { muteErrorAndResult, useDateTimePicker, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectAccountById, selectDefaultCurrency } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import SettingsItemSelectable from 'design-system/SettingsItemSelectable';
import { focus, useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { Spacer } from 'design-system/Spacer';
import { SCROLL_INDICATOR_INSETS } from 'design-system';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { isWeb } from '../../constants';
const goalToState = (goal, dateFormat) => {
    let date = goal?.targetDate;
    if (isWeb && date) {
        date = format(parseISO(date), dateFormat);
    }
    return {
        targetAmount: Math.ceil(goal?.targetAmount || 0),
        targetDate: date,
    };
};
const SavingGoalEditScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { accountId, goalId } = route.params;
    const dispatch = useAppDispatch();
    const loadedGoals = useAppSelector((store) => store.accounts.loadedGoals);
    useEffect(() => {
        if (!loadedGoals) {
            dispatch(getAccountGoals());
        }
    }, [loadedGoals]);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const monthFirst = guessedHomeCountry === 'US' || guessedHomeCountry === 'CA';
    const dateFormat = monthFirst ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
    const colors = useColors();
    const goal = useAppSelector((store) => store.accounts.goals[accountId]);
    const [state, setState] = useState(() => goalToState(goal, dateFormat));
    const { targetDate, targetAmount } = state;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerRight: goalId
                ? () => (<NavigationHeaderRightIconButton onPress={() => {
                        Alert.alert(`Delete goal?`, 'Would you like to delete this goal?', [
                            { text: 'Cancel', style: 'cancel' },
                            {
                                text: 'Delete',
                                onPress: () => {
                                    dispatch(deleteGoal(goalId, accountId));
                                    navigation.goBack();
                                },
                                style: 'destructive',
                            },
                        ]);
                    }}>
                  <IconTrash colorKey="primary"/>
                </NavigationHeaderRightIconButton>)
                : undefined,
        });
    }, [accountId, colors.text.primary, goalId]);
    const onSetTargetAmount = useCallback((_formatted, extracted) => {
        const targetAmount = extracted ? parseFloat(extracted) : 0;
        setState((state) => ({ ...state, targetAmount }));
    }, []);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const valueRef = useRef(null);
    const dateRef = useRef(null);
    useAutoFocusInput(valueRef);
    const dateErrorText = useMemo(() => {
        if (isWeb && targetDate?.length === 10) {
            const parsedDate = parse(targetDate, dateFormat, new Date());
            if (!isValid(parsedDate)) {
                return 'Enter a valid date.';
            }
            if (isBefore(parsedDate, new Date())) {
                return 'The date must be in the future.';
            }
        }
        return undefined;
    }, [targetDate, dateFormat]);
    const save = useCallback(async () => {
        const { targetAmount, targetDate } = state;
        if (!targetAmount) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must specify a target amount', undefined, { onDismiss: () => focus(valueRef) });
            return;
        }
        let isoDate = targetDate;
        if (isWeb && targetDate) {
            const split = targetDate.split('/');
            if (split.length !== 3) {
                Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must specify a valid date', undefined, { onDismiss: () => focus(dateRef) });
                return;
            }
            const month = parseInt(split[monthFirst ? 0 : 1], 10) - 1;
            const day = parseInt(split[monthFirst ? 1 : 0], 10);
            const year = parseInt(split[2], 10);
            const date = new Date(year, month, day);
            if (isValid(date)) {
                isoDate = formatISO(date);
            }
            else {
                Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must specify a valid date', undefined, { onDismiss: () => focus(dateRef) });
                return;
            }
        }
        if (isoDate && isBefore(isoDate, new Date())) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'The date must be in the future.', undefined, { onDismiss: () => focus(dateRef) });
            return;
        }
        if (isWeb && dateErrorText) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), dateErrorText, undefined, { onDismiss: () => focus(dateRef) });
            return;
        }
        if (goal?.targetAmount === targetAmount &&
            goal?.targetDate === isoDate) {
            navigation.goBack();
            return;
        }
        Keyboard.dismiss();
        if (goal) {
            const res = await fetch(editGoal(goal.id, {
                accountId,
                targetAmount,
                targetDate: isoDate
                    ? format(parseISO(isoDate), 'yyyy-MM-dd')
                    : undefined,
            }));
            if (!res)
                return;
            await fetch(getAccountGoals());
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            else {
                navigation.navigateDeprecated('Transactions', { id: accountId });
            }
        }
        else {
            const res = await fetch(addGoal({
                accountId,
                targetAmount,
                targetDate: isoDate
                    ? format(parseISO(isoDate), 'yyyy-MM-dd')
                    : undefined,
            }));
            if (!res)
                return;
            fetch(getAccountGoals());
            navigation.navigateDeprecated('Success', {
                subTitle: 'Your goal has been created!',
                onContinue: () => {
                    if (navigation.canGoBack()) {
                        navigation.pop(2);
                    }
                    else {
                        navigation.navigateDeprecated('Transactions', {
                            id: accountId,
                        });
                    }
                    return true;
                },
            });
        }
    }, [state, dateErrorText, goal, monthFirst, accountId]);
    const onPressSave = useRequiredBenefitCallbackWithSpaces(save, 'savingGoals');
    const minDate = useMemo(() => addDays(new Date(), 1), []);
    const { showDateTimePicker } = useDateTimePicker({
        initialValue: state.targetDate ? parseISO(state.targetDate) : minDate,
        minimumDate: minDate,
        onSubmitDate: (date) => {
            setState((oldState) => ({
                ...oldState,
                targetDate: date.toISOString(),
            }));
        },
    });
    const account = useAppSelector((store) => selectAccountById(store, accountId));
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const displayDate = useMemo(() => targetDate && !isWeb && format(parseISO(targetDate), dateFormat), [dateFormat, targetDate]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const formContent = (<>
          <SettingsItemSelectable>
            <SettingsItemMoneyInput ref={valueRef} icon={<IconGoal gradientKey="skyBlue"/>} value={targetAmount ? String(targetAmount) : ''} shouldUsePlaceholderColor={!targetAmount} onChangeText={onSetTargetAmount} onClearTextInput={() => setState((state) => ({ ...state, targetAmount: 0 }))} description="Goal amount" prefix={getSymbolFromCurrency(goal?.currency ||
            (account?.nativeBalance
                ? account?.nativeBalance.currency
                : account?.currency) ||
            defaultCurrency)} placeholder="Choose..." maximumFractionalDigits={0} maximumIntegerDigits={10}/>
          </SettingsItemSelectable>
          <SettingsItemSelectable errorText={dateErrorText}>
            {isWeb ? (<SettingsItemInputMask ref={dateRef} icon={<IconBoldCalendar gradientKey="lightPink"/>} description="Target date (optional)" placeholder={dateFormat.toUpperCase()} value={targetDate} shouldUsePlaceholderColor={!targetDate} onChangeText={(text) => setState((s) => ({
                ...s,
                targetDate: text,
            }))} onClearTextInput={() => setState((state) => ({ ...state, targetDate: '' }))} type="datetime" returnKeyLabel="Done" returnKeyType="done" options={{ format: dateFormat }} keyboardType="number-pad"/>) : (<SettingsItemNavigate icon={<IconBoldCalendar gradientKey="lightPink"/>} description="Target date (optional)" onPress={showDateTimePicker} title={displayDate || 'Choose...'}/>)}
          </SettingsItemSelectable>
        </>);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content"/>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} keyboardShouldPersistTaps="handled">
            {isWeb ? (<>
                <Spacer h={8}/>
                {formContent}
              </>) : (<SettingsSection>{formContent}</SettingsSection>)}
          </Animated.ScrollView>
          <KeyboardAvoidingButton brand disabled={!targetAmount} onPress={onPressSave} title="Save" isFetching={isFetching}/>
        </AppView>);
}, ({ route, colors }) => ({
    title: route.params?.goalId ? 'Edit your goal' : 'Set a goal',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default SavingGoalEditScreen;
